import {
    deleteCallWidget,
    updateCallWidget,
} from 'flow/actions/run-url-actions';
import Loading from 'flow/components/ui-components/Loading';
import Page from 'flow/components/Setting/Page';
import Button from 'flow/components/ui-components/Button';
import Divider from 'flow/components/ui-components/Divider';
import InputField from 'flow/components/ui-components/InputField';
import RouteLink from 'flow/components/ui-components/RouteLink';
import useAppContext from 'flow/hooks/useAppContext';
import { CallwidgetParams } from 'flow/types/Params';
import { routes } from 'flow/types/routes';

import { generatePath } from 'flow/utils/router-utils';
import { ChangeEvent, useState } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { useHistory, useParams } from 'react-router-dom';

const CallRoutingCallWidgetsEdit = () => {
    const { i18next } = useAppContext();
    const { extensionKey, callwidgetKey } = useParams<CallwidgetParams>();
    const history = useHistory();
    const dispatch = useAppDispatch();
    const callWidget = useAppSelector((state) =>
        state.callWidgets?.get(callwidgetKey),
    );

    const initialTriggerType =
        callWidget?.get('triggerType') || 'onuserinteraction';
    const initialUrl = callWidget?.get('url') || '';
    const initialTitle = callWidget?.get('title') || '';
    const initialKey = callWidget?.get('key') || '';

    const [url, setUrl] = useState(initialUrl);
    const [title, setTitle] = useState(initialTitle);

    const handleInput = (event: ChangeEvent<HTMLInputElement>) => {
        const { id, value } = event.target;
        if (id === 'url') setUrl(value.trim());
        if (id === 'title') setTitle(value.trim());
    };

    const saveEditedCallWidgetAndGoBack = () => {
        dispatch(
            updateCallWidget({ title, url, initialTriggerType, initialKey }),
        );
        history.push(
            generatePath(routes['settings.softphone.advanced.callwidgets'], {
                extensionKey,
            }),
        );
    };

    const deleteCallWidgetAndGoBack = () => {
        dispatch(deleteCallWidget(callWidget.get('key')));
        history.push(
            generatePath(routes['settings.softphone.advanced.callwidgets'], {
                extensionKey,
            }),
        );
    };

    if (!callWidget) {
        return <Loading />;
    }

    return (
        <Page header={initialTitle} onClickBack={saveEditedCallWidgetAndGoBack}>
            <div>
                <p className="text-sm">
                    {i18next.t(
                        'settings.callrouting.call_widgets.add.description',
                    )}
                </p>
            </div>

            <Divider />

            <div className="flex flex-col gap-6">
                <InputField
                    label={
                        i18next.t(
                            'settings.callrouting.call_widgets.add.widget_title',
                        ) + '*'
                    }
                    defaultValue={title}
                    id={'title'}
                    onChange={handleInput}
                />

                <InputField
                    label={
                        i18next.t(
                            'settings.callrouting.call_widgets.add.widget_url',
                        ) + '*'
                    }
                    defaultValue={url}
                    id={'url'}
                    onChange={handleInput}
                />
            </div>

            <div
                className="mt-6 rounded-sm bg-gray-100 p-4 text-sm"
                dangerouslySetInnerHTML={{
                    __html: i18next.t(
                        'settings.callrouting.call_widgets.add.example',
                    ),
                }}
            />

            <Divider />

            <RouteLink
                to={generatePath(
                    routes[
                        'settings.softphone.advanced.callwidgets.callwidgetKey.triggertype'
                    ],
                    { extensionKey, callwidgetKey: callWidget?.get('key') },
                )}
                description={i18next.t(
                    `settings.callrouting.call_widgets.trigger_type.${initialTriggerType}`,
                )}
            >
                {i18next.t('settings.callrouting.call_widgets.trigger_type')}
            </RouteLink>

            <div className="px-2">
                <p className="text-xs text-gray-500">
                    {i18next.t('call_widgets.auto_open.hint')}
                </p>
            </div>

            <Divider />

            <div>
                <Button
                    onClick={deleteCallWidgetAndGoBack}
                    color="ghost"
                    className="px-2"
                >
                    {i18next.t('settings.callrouting.call_widgets.remove')}
                </Button>
            </div>
        </Page>
    );
};

export default CallRoutingCallWidgetsEdit;
